import { Box, Typography, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import { IconX } from '@tabler/icons';
import { NotificationCondition, FieldOption } from '../../types';
import { operatorDisplayNames } from '../../mocks/notificationData';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { InputMaskPhone } from 'ui-component/RecordView/components/InputMaskPhoneCell';
import { phoneCountryOptions } from 'views/Portal/Tabs/Request/assets';
import { useConditionSection } from '../../hooks/useConditionSection';
import { SimpleTextField } from './SimpleTextField';
import { SimpleAutocomplete } from './SimpleAutocomplete';

interface ConditionSectionProps {
    fields: FieldOption[];
    loadingFields: boolean;
    condition?: NotificationCondition;
    onChange: (condition: NotificationCondition | undefined) => void;
}

export const ConditionSection = ({ fields, loadingFields, condition, onChange }: ConditionSectionProps) => {
    const {
        selectedField,
        selectedOperator,
        value,
        currentField,
        operators,
        loadingOperators,
        listOptions,
        loadingListValues,
        handleFieldChange,
        handleOperatorChange,
        handleClearValue,
        setValue,
        isValueRequired,
        getInputType,
        getFieldCategory
    } = useConditionSection({ fields, loadingFields, condition, onChange });

    const renderValueInput = () => {
        const inputType = getInputType();
        const fieldCategory = getFieldCategory();
        const valueRequired = isValueRequired();
        const isDisabled = !selectedOperator || !valueRequired;
        const hasError = valueRequired && !value && !!selectedOperator;
        let placeholder = 'Required Field';
        if (!selectedOperator) {
            placeholder = 'Select an operator first';
        } else if (!valueRequired) {
            placeholder = 'Not required for this operator';
        }

        if (!valueRequired) {
            return (
                <SimpleTextField
                    data-testid="notification-condition-value-input"
                    fullWidth
                    size="small"
                    value=""
                    disabled
                    placeholder={placeholder}
                />
            );
        }

        if (fieldCategory === 'list') {
            return (
                <Box sx={{ position: 'relative', width: '100%' }}>
                    {loadingListValues && (
                        <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
                            <CircularProgress size={16} />
                        </Box>
                    )}
                    <SimpleAutocomplete
                        data-testid="notification-condition-value-input"
                        value={value}
                        onChange={setValue}
                        options={listOptions.map((option: ListValue) => ({
                            id: String(option.id),
                            displayName: option.value
                        }))}
                        disabled={isDisabled || loadingListValues}
                        error={hasError}
                        placeholder={loadingListValues ? 'Loading options...' : 'Required Field'}
                    />
                </Box>
            );
        }

        if (fieldCategory === 'boolean') {
            return (
                <SimpleAutocomplete
                    data-testid="notification-condition-value-input"
                    value={value}
                    onChange={setValue}
                    options={[
                        { id: 'true', displayName: 'True' },
                        { id: 'false', displayName: 'False' }
                    ]}
                    disabled={isDisabled}
                    error={hasError}
                    placeholder="Required Field"
                />
            );
        }

        if (fieldCategory === 'phone') {
            return (
                <Box data-testid="notification-condition-value-input" sx={{ width: '100%' }}>
                    <InputMaskPhone
                        name="phone-value"
                        value={value}
                        onChange={(e: any) => setValue(e.target.value)}
                        options={phoneCountryOptions}
                        disabled={isDisabled}
                        TextFieldComponent={SimpleTextField as any}
                        containerSx={{ width: '100%' }}
                        textFieldSx={{
                            '& .MuiInputBase-root': {
                                height: '2rem'
                            }
                        }}
                    />
                    {hasError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            Required Field
                        </Typography>
                    )}
                </Box>
            );
        }

        const isCurrencyField = currentField?.dataType.toLowerCase() === 'currency';
        const isObjectField = fieldCategory === 'object';

        return (
            <SimpleTextField
                data-testid="notification-condition-value-input"
                fullWidth
                size="small"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={isDisabled}
                error={hasError}
                placeholder={isObjectField ? 'Search in object properties (e.g., "John Doe", "email@domain.com")' : placeholder}
                type={inputType}
                helperText={isObjectField && !isDisabled ? 'Search will match any property value in the object' : undefined}
                InputProps={{
                    startAdornment:
                        isCurrencyField && !isDisabled ? (
                            <InputAdornment position="start">
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    USD
                                </Typography>
                            </InputAdornment>
                        ) : null,
                    endAdornment:
                        value && !isDisabled ? (
                            <InputAdornment position="end">
                                <IconButton
                                    data-testid="notification-condition-value-clear-button"
                                    size="small"
                                    onClick={handleClearValue}
                                    edge="end"
                                >
                                    <IconX size={16} />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                }}
            />
        );
    };

    return (
        <Box
            data-testid="notification-condition-section"
            sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                p: 2
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: '#1F2937',
                    fontSize: '16px'
                }}
            >
                Condition
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                {/* Field Column */}
                <Box>
                    <Typography
                        sx={{
                            mb: 0.5,
                            color: '#6B7280',
                            fontSize: '14px',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        Field
                        <Typography component="span" sx={{ color: '#EF4444' }}>
                            *
                        </Typography>
                    </Typography>
                    <SimpleAutocomplete
                        data-testid="notification-condition-field-select"
                        value={selectedField}
                        onChange={handleFieldChange}
                        options={fields.filter(({ id }) => Boolean(Number(id)))}
                        disabled={loadingFields}
                        error={!selectedField}
                        placeholder="Required Field"
                    />
                </Box>

                {/* Operator Column */}
                <Box>
                    <Typography
                        sx={{
                            mb: 0.5,
                            color: '#6B7280',
                            fontSize: '14px',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        Operator
                        <Typography component="span" sx={{ color: '#EF4444' }}>
                            *
                        </Typography>
                    </Typography>
                    <SimpleAutocomplete
                        data-testid="notification-condition-operator-select"
                        value={selectedOperator}
                        onChange={(selectedValue) => handleOperatorChange(selectedValue as any)}
                        options={operators.map((op) => ({ id: op, displayName: operatorDisplayNames[op] }))}
                        disabled={!selectedField || loadingOperators}
                        placeholder="Select an operator"
                    />
                </Box>

                {/* Value Column */}
                <Box>
                    <Typography
                        sx={{
                            mb: 0.5,
                            color: '#6B7280',
                            fontSize: '14px',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        Value
                        <Typography component="span" sx={{ color: '#EF4444' }}>
                            *
                        </Typography>
                    </Typography>
                    {renderValueInput()}
                </Box>
            </Box>
        </Box>
    );
};
